<script>
	import { onMount } from 'svelte';
	import { agentsAPI, aiProvidersAPI } from './api.js';
	import Icon from './lib/Icon.svelte';
	import AIAgentEditor from './AIAgentEditor.svelte';

	let agents = $state([]);
	let templates = $state([]);
	let providers = $state([]);
	let loading = $state(true);
	let error = $state('');
	let search = $state('');
	let filterStatus = $state('all');

	// Drawers
	let editingAgentId = $state(null);
	let creatingFromTemplate = $state(null); // template object or 'blank'
	let showTemplatesModal = $state(false);

	const STATUS_LABELS = {
		draft: 'Borrador',
		active: 'Activo',
		paused: 'Pausado',
		training: 'Entrenando'
	};
	const STATUS_BADGE = {
		draft: 'badge',
		active: 'badge-success',
		paused: 'badge-warning',
		training: 'badge-info'
	};

	let filteredAgents = $derived.by(() => {
		const term = search.trim().toLowerCase();
		return agents.filter(a => {
			if (filterStatus !== 'all' && a.status !== filterStatus) return false;
			if (term) {
				return (
					(a.name || '').toLowerCase().includes(term) ||
					(a.role || '').toLowerCase().includes(term) ||
					(a.description || '').toLowerCase().includes(term)
				);
			}
			return true;
		});
	});

	async function loadAll() {
		loading = true;
		error = '';
		try {
			const [a, t, p] = await Promise.all([
				agentsAPI.list(),
				agentsAPI.listTemplates(),
				aiProvidersAPI.getAll()
			]);
			agents = a || [];
			templates = t || [];
			providers = (p?.providers || p || []).filter(x => x.is_active);
		} catch (e) {
			error = e.message || 'Error cargando agentes';
		} finally {
			loading = false;
		}
	}

	function openTemplates() {
		showTemplatesModal = true;
	}

	function pickTemplate(t) {
		showTemplatesModal = false;
		creatingFromTemplate = t || 'blank';
	}

	async function handleAgentSaved(savedAgent, isNew) {
		await loadAll();
		creatingFromTemplate = null;
		if (isNew) {
			editingAgentId = savedAgent.id;
		}
	}

	async function toggleStatus(agent) {
		const next = agent.status === 'active' ? 'paused' : 'active';
		try {
			const updated = await agentsAPI.update(agent.id, { status: next });
			const idx = agents.findIndex(a => a.id === updated.id);
			if (idx >= 0) agents[idx] = updated;
			agents = [...agents];
		} catch (e) {
			error = e.message || 'No se pudo cambiar el estado';
		}
	}

	async function duplicate(agent) {
		try {
			await agentsAPI.duplicate(agent.id);
			await loadAll();
		} catch (e) {
			error = e.message || 'No se pudo duplicar';
		}
	}

	async function remove(agent) {
		if (!confirm(`¿Eliminar el agente "${agent.name}"? Esta acción es irreversible.`)) return;
		try {
			await agentsAPI.remove(agent.id);
			agents = agents.filter(a => a.id !== agent.id);
		} catch (e) {
			error = e.message || 'No se pudo eliminar';
		}
	}

	function roleIcon(role) {
		const map = {
			sales: 'sparkles', support: 'help-circle', qualifier: 'check-circle',
			faq: 'book-open', onboarding: 'sparkles', collections: 'phone',
			custom: 'bot'
		};
		return map[role] || 'bot';
	}

	onMount(loadAll);
</script>

{#if editingAgentId !== null || creatingFromTemplate !== null}
	<AIAgentEditor
		agentId={editingAgentId}
		template={creatingFromTemplate === 'blank' ? null : creatingFromTemplate}
		providers={providers}
		onClose={() => { editingAgentId = null; creatingFromTemplate = null; }}
		onSaved={handleAgentSaved}
	/>
{:else}
	<div class="agents-view">
		<!-- Toolbar -->
		<div class="toolbar">
			<div class="toolbar-left">
				<div class="search-box">
					<Icon name="search" size={14} class="search-icon" />
					<input type="text" placeholder="Buscar agentes..." bind:value={search} />
				</div>
				<div class="status-tabs">
					{#each [{k:'all',l:'Todos'},{k:'active',l:'Activos'},{k:'draft',l:'Borrador'},{k:'paused',l:'Pausados'}] as t}
						<button
							class:active={filterStatus === t.k}
							onclick={() => (filterStatus = t.k)}
						>{t.l}</button>
					{/each}
				</div>
			</div>
			<div class="toolbar-right">
				<button class="btn btn-secondary btn-sm" onclick={loadAll} disabled={loading}>
					<Icon name="refresh" size={14} />
					Actualizar
				</button>
				<button class="btn btn-primary btn-sm" onclick={openTemplates}>
					<Icon name="plus" size={14} />
					Crear agente
				</button>
			</div>
		</div>

		{#if error}
			<div class="banner-error">
				<Icon name="alert-circle" size={14} />
				<span>{error}</span>
				<button class="banner-close" onclick={() => (error = '')}><Icon name="x" size={14} /></button>
			</div>
		{/if}

		<!-- Body -->
		<div class="body">
			{#if loading}
				<div class="loading"><div class="spinner"></div></div>
			{:else if !providers.length}
				<div class="empty card card-padded">
					<Icon name="plug" size={32} />
					<h3>Configura primero un proveedor IA</h3>
					<p class="muted">
						Antes de crear agentes, agrega al menos un proveedor IA activo (Claude, OpenAI,
						Gemini, Llama local, etc.) en la sección "Proveedores IA".
					</p>
				</div>
			{:else if !filteredAgents.length}
				<div class="empty card card-padded">
					<Icon name="sparkles" size={32} />
					<h3>{agents.length ? 'Sin coincidencias' : 'Aún no tienes agentes'}</h3>
					<p class="muted">
						{agents.length
							? 'Ajusta los filtros o crea uno nuevo desde una plantilla.'
							: 'Empieza con una plantilla (Vendedor, Soporte, Calificador...) y personalízala.'}
					</p>
					{#if !agents.length}
						<button class="btn btn-primary" onclick={openTemplates}>
							<Icon name="plus" size={14} />
							Elegir plantilla
						</button>
					{/if}
				</div>
			{:else}
				<div class="grid">
					{#each filteredAgents as agent (agent.id)}
						<article class="agent-card card">
							<header class="card-head">
								<div class="ident">
									<div class="ag-avatar">
										<Icon name={roleIcon(agent.role)} size={20} />
									</div>
									<div class="ident-text">
										<h3>{agent.name}</h3>
										<div class="meta">
											<span class="badge {STATUS_BADGE[agent.status]}">
												{STATUS_LABELS[agent.status] || agent.status}
											</span>
											<span class="muted-sm">{agent.role}</span>
										</div>
									</div>
								</div>
								<div class="actions">
									<button class="icon-btn" title="Duplicar" onclick={() => duplicate(agent)}>
										<Icon name="plus" size={14} />
									</button>
									<button class="icon-btn danger" title="Eliminar" onclick={() => remove(agent)}>
										<Icon name="trash" size={14} />
									</button>
								</div>
							</header>

							{#if agent.description}
								<p class="desc">{agent.description}</p>
							{:else if agent.persona}
								<p class="desc">{agent.persona.slice(0, 140)}{agent.persona.length > 140 ? '...' : ''}</p>
							{/if}

							<div class="cores">
								<div class="core">
									<span class="core-label">Núcleo</span>
									<span class="core-name">{agent.primary_provider?.name || '—'}</span>
								</div>
								{#if agent.fallback_provider}
									<div class="core">
										<span class="core-label">Fallback</span>
										<span class="core-name">{agent.fallback_provider.name}</span>
									</div>
								{/if}
							</div>

							<div class="kpis-row">
								<div class="kpi">
									<div class="kpi-num">{agent.total_interactions || 0}</div>
									<div class="kpi-label">Interacciones</div>
								</div>
								<div class="kpi">
									<div class="kpi-num">{agent.satisfaction != null ? agent.satisfaction + '%' : '—'}</div>
									<div class="kpi-label">Satisfacción</div>
								</div>
								<div class="kpi">
									<div class="kpi-num">{agent.handoff_count || 0}</div>
									<div class="kpi-label">Handoffs</div>
								</div>
							</div>

							{#if agent.allowed_tools?.length}
								<div class="tools-row">
									{#each agent.allowed_tools.slice(0, 4) as t}
										<span class="tool-chip">{t}</span>
									{/each}
									{#if agent.allowed_tools.length > 4}
										<span class="tool-chip more">+{agent.allowed_tools.length - 4}</span>
									{/if}
								</div>
							{/if}

							<footer class="card-foot">
								<button class="btn btn-secondary btn-sm" onclick={() => toggleStatus(agent)}>
									<Icon name={agent.status === 'active' ? 'circle' : 'check'} size={13} />
									{agent.status === 'active' ? 'Pausar' : 'Activar'}
								</button>
								<button class="btn btn-primary btn-sm" onclick={() => (editingAgentId = agent.id)}>
									Configurar
									<Icon name="arrow-right" size={13} />
								</button>
							</footer>
						</article>
					{/each}
				</div>
			{/if}
		</div>
	</div>
{/if}

<!-- ===== Templates picker modal ===== -->
{#if showTemplatesModal}
	<div class="modal-back" onclick={() => (showTemplatesModal = false)} role="presentation">
		<div class="templates-modal" onclick={(e) => e.stopPropagation()} role="dialog">
			<div class="modal-head">
				<div>
					<h3>Crear nuevo agente</h3>
					<p class="muted">Empieza con una plantilla o construye desde cero.</p>
				</div>
				<button class="icon-btn" onclick={() => (showTemplatesModal = false)} aria-label="Cerrar">
					<Icon name="x" size={16} />
				</button>
			</div>
			<div class="templates-grid">
				{#each templates as t}
					<button class="tpl-card" onclick={() => pickTemplate(t)}>
						<div class="tpl-icon"><Icon name={t.icon || 'bot'} size={20} /></div>
						<h4>{t.name}</h4>
						<p>{t.description}</p>
					</button>
				{/each}
				<button class="tpl-card blank" onclick={() => pickTemplate('blank')}>
					<div class="tpl-icon"><Icon name="plus" size={20} /></div>
					<h4>Desde cero</h4>
					<p>Configura cada detalle manualmente.</p>
				</button>
			</div>
		</div>
	</div>
{/if}

<style>
	.agents-view {
		height: 100%;
		display: flex;
		flex-direction: column;
		background: var(--surface-1);
	}
	.toolbar {
		display: flex; align-items: center; justify-content: space-between;
		gap: 12px; padding: 14px 24px;
		background: var(--surface-0);
		border-bottom: 1px solid var(--border-1);
		flex-shrink: 0;
	}
	.toolbar-left { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; }
	.toolbar-right { display: flex; gap: 8px; }
	.search-box { position: relative; max-width: 260px; flex: 1; }
	.search-box :global(.search-icon) {
		position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
		color: var(--text-3); pointer-events: none;
	}
	.search-box input { padding-left: 32px; height: 32px; font-size: 13px; }
	.status-tabs {
		display: flex;
		background: var(--surface-2);
		border-radius: 8px;
		padding: 3px;
	}
	.status-tabs button {
		padding: 5px 12px;
		font-size: 12px;
		font-weight: 600;
		color: var(--text-3);
		border-radius: 5px;
		transition: background var(--transition-fast), color var(--transition-fast);
	}
	.status-tabs button.active { background: var(--surface-0); color: var(--text-1); box-shadow: var(--shadow-xs); }

	.banner-error {
		display: flex; align-items: center; gap: 8px;
		background: var(--danger-soft); color: var(--danger);
		padding: 8px 16px; font-size: 13px;
		border-bottom: 1px solid var(--border-1);
	}
	.banner-close {
		margin-left: auto; width: 22px; height: 22px;
		display: flex; align-items: center; justify-content: center;
		border-radius: 4px; color: var(--danger);
	}
	.banner-close:hover { background: rgba(239, 68, 68, 0.15); }

	.body {
		flex: 1;
		overflow-y: auto;
		padding: 18px 24px 24px;
	}
	.loading { display: flex; align-items: center; justify-content: center; padding: 60px; }
	.spinner {
		width: 32px; height: 32px;
		border: 3px solid var(--border-1);
		border-top-color: var(--accent);
		border-radius: 50%;
		animation: spin 700ms linear infinite;
	}
	@keyframes spin { to { transform: rotate(360deg); } }

	.empty {
		max-width: 520px;
		margin: 60px auto 0;
		text-align: center;
		display: flex; flex-direction: column; align-items: center; gap: 10px;
		color: var(--text-3);
	}
	.empty h3 { color: var(--text-1); margin-top: 4px; }
	.empty p { color: var(--text-3); font-size: 14px; line-height: 1.5; }
	.muted { color: var(--text-3); }
	.muted-sm { color: var(--text-3); font-size: 12px; }

	.grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
		gap: 14px;
	}

	/* Agent card */
	.agent-card {
		padding: 18px;
		display: flex; flex-direction: column; gap: 12px;
		transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
	}
	.agent-card:hover {
		border-color: var(--border-2);
		box-shadow: var(--shadow-sm);
	}
	.card-head {
		display: flex; justify-content: space-between; gap: 10px;
	}
	.ident { display: flex; gap: 12px; min-width: 0; }
	.ag-avatar {
		width: 40px; height: 40px;
		border-radius: 10px;
		background: linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%);
		color: #fff;
		display: flex; align-items: center; justify-content: center;
		flex-shrink: 0;
	}
	.ident-text { min-width: 0; }
	.ident-text h3 { font-size: 14px; margin-bottom: 4px; }
	.meta { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
	.actions { display: flex; gap: 4px; }
	.icon-btn {
		width: 28px; height: 28px;
		display: flex; align-items: center; justify-content: center;
		border-radius: 6px;
		color: var(--text-3);
		transition: background var(--transition-fast), color var(--transition-fast);
	}
	.icon-btn:hover { background: var(--surface-2); color: var(--text-1); }
	.icon-btn.danger:hover { background: var(--danger-soft); color: var(--danger); }

	.desc {
		font-size: 12.5px;
		color: var(--text-2);
		line-height: 1.5;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.cores {
		display: flex; gap: 12px;
		font-size: 12px;
		color: var(--text-2);
	}
	.core { display: flex; flex-direction: column; gap: 2px; }
	.core-label { font-size: 11px; color: var(--text-3); font-weight: 500; }
	.core-name { font-weight: 600; color: var(--text-1); }

	.kpis-row {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 8px;
		padding: 10px 0;
		border-top: 1px solid var(--border-1);
		border-bottom: 1px solid var(--border-1);
	}
	.kpi { text-align: center; }
	.kpi-num { font-size: 16px; font-weight: 700; color: var(--text-1); }
	.kpi-label { font-size: 10.5px; color: var(--text-3); }

	.tools-row {
		display: flex; gap: 4px; flex-wrap: wrap;
	}
	.tool-chip {
		font-size: 10.5px;
		padding: 3px 8px;
		background: var(--accent-soft);
		color: var(--accent);
		border-radius: 4px;
		font-weight: 500;
	}
	.tool-chip.more { background: var(--surface-2); color: var(--text-3); }

	.card-foot {
		display: flex; gap: 8px; justify-content: flex-end;
	}

	/* Modal */
	.modal-back {
		position: fixed; inset: 0;
		background: rgba(15, 23, 42, 0.5);
		display: flex; align-items: center; justify-content: center;
		z-index: 100;
	}
	.templates-modal {
		background: var(--surface-0);
		border-radius: 14px;
		box-shadow: var(--shadow-xl);
		width: 100%;
		max-width: 720px;
		max-height: 85vh;
		overflow-y: auto;
	}
	.modal-head {
		display: flex; align-items: flex-start; justify-content: space-between;
		padding: 20px 24px;
		border-bottom: 1px solid var(--border-1);
	}
	.modal-head h3 { font-size: 17px; margin-bottom: 2px; }
	.modal-head p { font-size: 13px; }
	.templates-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
		gap: 12px;
		padding: 20px 24px 24px;
	}
	.tpl-card {
		background: var(--surface-1);
		border: 1px solid var(--border-1);
		border-radius: 10px;
		padding: 16px;
		text-align: left;
		display: flex; flex-direction: column; gap: 8px;
		cursor: pointer;
		transition: all var(--transition-fast);
	}
	.tpl-card:hover {
		background: var(--surface-0);
		border-color: var(--accent);
		box-shadow: var(--shadow-sm);
		transform: translateY(-1px);
	}
	.tpl-card.blank { border-style: dashed; }
	.tpl-icon {
		width: 36px; height: 36px;
		border-radius: 9px;
		background: var(--accent-soft);
		color: var(--accent);
		display: flex; align-items: center; justify-content: center;
	}
	.tpl-card h4 { font-size: 14px; margin: 0; }
	.tpl-card p { font-size: 12.5px; color: var(--text-3); line-height: 1.5; margin: 0; }
</style>
